import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  ForgeDialog,
  ForgeIcon,
  ForgeIconButton,
  ForgeScaffold,
  ForgeTextField,
  ForgeToolbar
} from '@tylertech/forge-react';
import './index.scss';
import AttachmentSection from './components/Attachment/AttachmentSection';
import Footer from './components/Footer';
import FrequencySelector from '../FrequencySelector';
import List from './components/Preview/Attachment/List';
import PreviewFooter from './components/Preview/Footer';
import RecipientsField from './components/RecipientsField/RecipientsField';
import SummarySection from './components/Summary/SummarySection';
import { FrequencyConfig } from 'common/types/frequencySelector/frequencySelector';
import { I18n } from 'common/visualizations';
import { RequiredFieldsStatus, ScheduleSendValues } from './types/scheduleSend';
import { View } from 'common/types/view';
import {
  buildPayload,
  buildFrequencyConfigFromSubscription,
  buildRecipientsFromSubscriptions
} from './builder';
import { frequencyConfigDefaulValues, requiredFieldsStatusDefaultValues } from './constants/defaultVal';
import { showSuccessToastNow } from 'common/components/ToastNotification/Toastmaster';
import { tablesFromStoryData } from './components/Attachment/helpers';
import { useEscapeKey } from 'common/visualizations/views/agGridReact/customHooks/useEscapeKey';
import { defaultPDFFullReport, scheduledSendDefaultValues } from './helpers';
import { fetchExistingSubscriptions, fetchUsers, postScheduleDeliveryRequest } from './services';
import { withEmptyRequiredField } from './validators';
import InteractiveUser from 'common/types/users/interactiveUser';

const scope = 'shared.components.schedule_send_report.modal';

export interface ScheduleSendProps {
  view: View;
  onDismiss: () => void;
  scheduledSendModalVisible: boolean;
  setScheduledSendModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  currentUser: InteractiveUser;
}

type ModalMode = 'create' | 'preview' | 'edit' | 'update' | 'delete';

const useModalMode = (initialMode: ModalMode = 'create') => {
  const [currentMode, setCurrentMode] = useState<ModalMode>(initialMode);
  const isReadOnlyMode = currentMode === 'preview';

  return { currentMode, setCurrentMode, isReadOnlyMode };
};

const ScheduleSendReportModal: FC<ScheduleSendProps> = (props) => {
  const { onDismiss, scheduledSendModalVisible, setScheduledSendModalVisible, view, currentUser } = props;
  const defaultValues = scheduledSendDefaultValues(view);
  const [scheduleSendValues, setScheduleSendValues] = useState<ScheduleSendValues>(defaultValues);
  const [frequencyConfig, setFrequencyConfig] = useState<FrequencyConfig>(frequencyConfigDefaulValues);
  const { currentMode, setCurrentMode, isReadOnlyMode } = useModalMode('create');

  const [isFrequencyTimeInvalid, setIsFrequencyTimeInvalid] = useState(false);
  const [isFrequencyStartDateInvalid, setIsFrequencyStartDateInvalid] = useState(false);
  const [isFrequencyEndDateInvalid, setIsFrequencyEndDateInvalid] = useState(false);
  const [isRecipientEmptyUponSaving, setIsRecipientEmptyUponSaving] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isIntervalInvalid, setIsIntervalInvalid] = useState(false);
  const [requiredFieldsStatus, setRequiredFieldsStatus] = useState<RequiredFieldsStatus>(
    requiredFieldsStatusDefaultValues
  );

  /**
   * Updates a specific key in the ScheduleSendValues state.
   *
   * @template K - A key of ScheduleSendValues: 'name', 'subject', 'recipients', 'attachmentConfigs', or 'frequencyConfig'.
   * @param key - The specific field to update in ScheduleSendValues.
   *   - `'name'`: string
   *   - `'subject'`: string
   *   - `'recipients'`: string[]
   *   - `'attachmentConfigs'`: AttachmentConfig[]
   *   - `'frequencyConfig'`: FrequencyConfig
   * @param value - The new value for the specified key, matching its expected type.
   *
   * @example
   * updateScheduleSendValue('name', 'My Schedule');
   * updateScheduleSendValue('recipients', ['user@example.com']);
   */
  const updateScheduleSendValue = useCallback(
    <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => {
      setScheduleSendValues((prev) => ({
        ...prev,
        [key]: value
      }));
    },
    []
  );

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const initializeModal = async () => {
      try {
        const subscriptions = await fetchExistingSubscriptions();
        if (!isMounted) return;
        if (subscriptions.length > 0) {
          const recipients = await fetchUsers(view);
          const frequencyConfigValues = buildFrequencyConfigFromSubscription(subscriptions[0]);
          const recipientsMap = buildRecipientsFromSubscriptions(subscriptions, recipients);
          setCurrentMode('preview');
          setFrequencyConfig(frequencyConfigValues);
          setScheduleSendValues({
            attachmentConfigs: [defaultPDFFullReport],
            frequencyConfig: frequencyConfigValues,
            name: '',
            recipients: recipientsMap,
            subject: ''
          });
        } else {
          setCurrentMode('create');
          setScheduleSendValues(scheduledSendDefaultValues(view));
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error loading subscriptions:', error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeModal();

    return () => {
      isMounted = false;
    };
  }, [view]);

  useEffect(() => {
    updateScheduleSendValue('frequencyConfig', frequencyConfig);
  }, [frequencyConfig]);

  // If a user clicks the esc button, we are closing the modal
  useEscapeKey(onDismiss, scheduledSendModalVisible);

  const onSaveConfiguration = async () => {
    if (
      !withEmptyRequiredField({
        scheduleSendValues,
        setRequiredFieldsStatus
      })
    ) {
      postScheduleDeliveryRequest(scheduleSendValues, currentUser, setScheduledSendModalVisible);
    }
  };

  const onPreview = async () => {
    setCurrentMode('preview');
  };

  const onEdit = async () => {
    setCurrentMode('edit');
    updateScheduleSendValue('name', view.name);
    updateScheduleSendValue('subject', view.name);
  };

  const onDelete = async () => {
    setCurrentMode('delete');
    onDismiss();
    showSuccessToastNow(I18n.t('delete_toast', { scope }));
  };

  const onUpdate = async () => {
    if (
      !withEmptyRequiredField({
        scheduleSendValues,
        setRequiredFieldsStatus
      })
    ) {
      setCurrentMode('update');
      const payload = await buildPayload(scheduleSendValues, currentUser);
      console.info('scheduled delivery payload: ', payload);
      onDismiss();
      showSuccessToastNow(I18n.t('update_toast', { scope }));
    }
  };

  const textFieldAttrs = {
    className: isReadOnlyMode ? 'read-only' : '',
    disabled: isReadOnlyMode,
    floatLabel: isReadOnlyMode,
    labelPosition: isReadOnlyMode ? 'inset' : 'block-start',
    required: !isReadOnlyMode,
    variant: isReadOnlyMode ? 'plain' : 'outlined'
  };

  return (
    <ForgeDialog
      open={scheduledSendModalVisible}
      on-forge-dialog-close={onDismiss}
      persistent
      data-testid="schedule-send-dialog"
      aria-labelledby="schedule-send-modal-title"
    >
      {isLoading ? null : (
        <ForgeScaffold className="schedule-send-modal">
          <div slot="header">
            <ForgeToolbar className="dialog-header tyl-typography">
              <h1
                slot="start"
                className="forge-typography--heading4"
                id="schedule-send-modal-title"
                data-testid="schedule-send-modal-title"
              >
                {I18n.t('title', { scope })}
              </h1>
              <ForgeIconButton densityLevel={3} slot="end">
                <ForgeIcon name="close" onClick={onDismiss} />
              </ForgeIconButton>
            </ForgeToolbar>
          </div>

          <div
            slot="body"
            className={`schedule-send-modal-body ${isReadOnlyMode ? 'preview-mode' : ''}`}
            data-testid="schedule-send-modal-body"
          >
            <ForgeTextField {...textFieldAttrs} invalid={!scheduleSendValues.name}>
              <label htmlFor="schedule-name-field">{I18n.t('schedule_name_label', { scope })}</label>
              <input
                id="schedule-name-field"
                data-testid="schedule-name-field"
                type="text"
                value={scheduleSendValues.name ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateScheduleSendValue('name', event.target.value);
                }}
                readOnly={isReadOnlyMode}
              />
              {!scheduleSendValues.name && !isReadOnlyMode && (
                <span slot="support-text">{I18n.t('required_field', { scope })}</span>
              )}
            </ForgeTextField>
            <ForgeTextField {...textFieldAttrs} invalid={!scheduleSendValues.subject}>
              <label htmlFor="email-subject-field">{I18n.t('email_subject_label', { scope })}</label>
              <input
                id="email-subject-field"
                data-testid="email-subject-field"
                type="text"
                value={scheduleSendValues.subject ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateScheduleSendValue('subject', event.target.value);
                }}
                readOnly={isReadOnlyMode}
              />
              {!scheduleSendValues.subject && !isReadOnlyMode && (
                <span slot="support-text">{I18n.t('required_field', { scope })}</span>
              )}
            </ForgeTextField>

            <RecipientsField
              key={`recipients-${view.id}`}
              view={view}
              scheduledSendModalVisible={scheduledSendModalVisible}
              recipients={scheduleSendValues.recipients}
              updateScheduleSendValue={updateScheduleSendValue}
              readOnly={isReadOnlyMode}
              isRecipientEmptyUponSaving={requiredFieldsStatus.isRecipientEmptyUponSaving}
            />

            {isReadOnlyMode ? (
              <List attachments={scheduleSendValues.attachmentConfigs} />
            ) : (
              <>
                <AttachmentSection
                  tableList={tablesFromStoryData(view)}
                  attachmentConfigs={scheduleSendValues.attachmentConfigs}
                  updateScheduleSendValue={updateScheduleSendValue}
                />
                <FrequencySelector
                  value={frequencyConfig}
                  setValue={setFrequencyConfig}
                  className="container-border"
                  isFrequencyTimeInvalid={requiredFieldsStatus.isFrequencyTimeInvalid}
                  isFrequencyStartDateInvalid={requiredFieldsStatus.isFrequencyStartDateInvalid}
                  isFrequencyEndDateInvalid={requiredFieldsStatus.isFrequencyEndDateInvalid}
                  isIntervalInvalid={requiredFieldsStatus.isIntervalInvalid}
                />
              </>
            )}
            <SummarySection scheduleSendValues={scheduleSendValues} />
          </div>

          <div slot="footer" className="schedule-send-footer">
            {isReadOnlyMode ? (
              <PreviewFooter onDelete={onDelete} onDismiss={onDismiss} onEdit={onEdit} />
            ) : (
              <Footer
                onDismiss={onDismiss}
                onPreview={onPreview}
                onSaveConfiguration={onSaveConfiguration}
                onUpdate={onUpdate}
                currentMode={currentMode}
                scheduleSendValues={scheduleSendValues}
              />
            )}
          </div>
        </ForgeScaffold>
      )}
    </ForgeDialog>
  );
};

export default ScheduleSendReportModal;
